/**
 * Content Engine Routing
 *
 * Pure routing helpers for Chat-native Content Engine tool selection.
 *
 * Primary responsibilities:
 * - Detect Content Engine workflow and context intents
 * - Choose the safest ContentEngine tool for a user continuation
 *
 * Dependencies:
 * - None
 */

/**
 * Temporary product kill switch — remove Content Engine from the tool
 * catalog and chat routing until we re-enable it. Flip to `false` to restore.
 */
export const CONTENT_ENGINE_TEMPORARILY_DISABLED = true

export type ContentEngineToolName =
  | 'ContentEngine__createCampaignDraft'
  | 'ContentEngine__approveCampaignAndCreateAssetPack'
  | 'ContentEngine__approveAssetPackAndGenerateVisuals'
  | 'ContentEngine__getContext'

function normalizeMessage(value: string) {
  return value
    .toLowerCase()
    .replace(/@\[[^\]]+\]\(super-agent:engines\)/g, '@engines')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

// --------------

export function isContentEngineMessage(message: string) {
  if (CONTENT_ENGINE_TEMPORARILY_DISABLED) return false

  const normalized = normalizeMessage(message)

  return (
    normalized.includes('content engine') ||
    normalized.includes('contentengine') ||
    normalized.includes('content system')
  )
}

// --------------

export function shouldSuppressEngineProductCreationForContentEngine(message: string) {
  if (CONTENT_ENGINE_TEMPORARILY_DISABLED) return false

  const normalized = normalizeMessage(message)

  return normalized.includes('@engines') && isContentEngineMessage(message)
}

// --------------

export function getContentEngineToolNameForMessage(
  message: string
): ContentEngineToolName | null {
  if (CONTENT_ENGINE_TEMPORARILY_DISABLED) return null

  const normalized = normalizeMessage(message)

  if (!isContentEngineMessage(message)) {
    return null
  }

  if (
    normalized.includes('contentengine approveassetpackandgeneratevisuals') ||
    /\bapprove(?:d)?\s+(?:the\s+)?content engine asset pack\b/.test(normalized)
  ) {
    return 'ContentEngine__approveAssetPackAndGenerateVisuals'
  }

  if (
    normalized.includes('contentengine approvecampaignandcreateassetpack') ||
    /\bapprove(?:d)?\s+(?:the\s+)?content engine campaign\b/.test(normalized)
  ) {
    return 'ContentEngine__approveCampaignAndCreateAssetPack'
  }

  if (
    normalized.includes('contentengine getcontext') ||
    normalized.includes('content engine handoff:') ||
    /\b(pull|load|inspect|latest|continue|improve|edit|output ops|brand memory|generated media|generated image)\b/.test(
      normalized
    )
  ) {
    return 'ContentEngine__getContext'
  }

  if (
    /\b(create|draft|generate|make|run|start|build|plan|write|produce)\b/.test(normalized) ||
    normalized.includes('@engines')
  ) {
    return 'ContentEngine__createCampaignDraft'
  }

  return null
}

// --------------

export function shouldEnableContentEngineTools(message: string) {
  if (CONTENT_ENGINE_TEMPORARILY_DISABLED) return false
  return isContentEngineMessage(message)
}
